import { ISunnyRequestBuilderConstructor, SunnyRequestBuilder } from '@/models/Common/SunnyRequestBuilder';
import {
  IIntegratedAutocompleteStationSuggestionRequestParams,
  IIntegratedAutocompleteSuggestionGlobalRequestParams,
  IIntegratedAutocompleteSuggestionRequestParams,
} from '@/models/IntegratedAutocomplete/interfaces';
import IntegratedAutocompleteStationSuggestionJSC from '@/schema/IntegratedAutocompleteStationSuggestionJSC';
import IntegratedAutocompleteSuggestionGlobalJSC from '@/schema/IntegratedAutocompleteSuggestionGlobalJSC';
import IntegratedAutocompleteSuggestionJSC from '@/schema/IntegratedAutocompleteSuggestionJSC';
import { IHTTPParams, IRequestWrap } from '@/server/IServer';
import { TReqConfigValidated } from '@/services/JSCService';
import { IAxiosRequesterConfig, THTTPMethods } from '@/services/RequestService/requesters/AxiosRequester';
import { AxiosRequestConfig } from 'axios';
import qs from 'qs';
import UUID from 'uuid';

export default class IntegratedAutocompleteRequestBuilder extends SunnyRequestBuilder {
  static fromReq<T extends SunnyRequestBuilder>(this: ISunnyRequestBuilderConstructor<T>, req: IRequestWrap): T {
    if (!req || !req.config) {
      return new this();
    }

    const device = req.ua.getDevice();
    return new this({
      accessToken: req.config.SearchAccessToken,
      baseURI: this.getBaseURIFromReq(req),
      cgntId: req.cookies['cgntId'] || req.sunnyMeta['cgntId'],
      clientIP: (req.headers['x-forwarded-for'] as string) || req.connection.remoteAddress,
      platform: device.type === 'mobile' || device.type === 'tablet' ? 'MWeb' : 'Web',
      userAgent: req.ua.getUA(),
      ywt: req.session.getYWT(),
      testProduct: req.session.getSettings?.().testProduct,
      traceID: (req.headers['X-Trace-Id'] as string) || UUID.v4(),
      expGroups: req.serviceConfig?.serverExpGroups ?? '',
    });
  }

  static getBaseURIFromReq(req: IRequestWrap): URI {
    return req.config.IntegratedAutocomplete;
  }

  createGetDomesticSuggestionsAccommodationList(
    reqParams: IIntegratedAutocompleteSuggestionRequestParams,
  ): TReqConfigValidated {
    return this.validate(IntegratedAutocompleteSuggestionJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/domestic/suggestions/accommodation', params),
    );
  }

  createGetDomesticSuggestionsLeisureList(
    reqParams: IIntegratedAutocompleteSuggestionRequestParams,
  ): TReqConfigValidated {
    return this.validate(IntegratedAutocompleteSuggestionJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/domestic/suggestions/leisure', params),
    );
  }

  createPostGlobalSuggestionsLocationList(
    reqParams: IIntegratedAutocompleteSuggestionGlobalRequestParams,
  ): TReqConfigValidated {
    return this.validate(IntegratedAutocompleteSuggestionGlobalJSC, reqParams).map((params) =>
      this.makeConfig('POST', '/global/suggestions/location', params),
    );
  }

  /**
   * 기차 자동완성 API
   * @see {@link https://yanolja.atlassian.net/wiki/spaces/YASEARCH/pages/72500001/NEW+API+-}
   */
  createGetTrainStationSuggestionsList(
    reqParams: IIntegratedAutocompleteStationSuggestionRequestParams,
  ): TReqConfigValidated {
    return this.validate(IntegratedAutocompleteStationSuggestionJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/domestic/suggestions/station', params),
    );
  }

  get CommonHeaders(): Record<string, string | undefined> {
    return {
      ...super.CommonHeaders,
      Authorization: `Bearer ${this.AccessTokenObject['access-token']}`,
      'X-Caller': 'sunny-web',
    };
  }

  protected getAPIEndpoint(path: string): string {
    const baseURI = this.baseURI.href();
    return baseURI ? `${baseURI}/v1${path}` : `${baseURI}/api/v1${path}`;
  }

  protected makeConfig(
    method: THTTPMethods,
    url: string,
    params: IHTTPParams = {},
    overrides?: AxiosRequestConfig,
    endpointBuilder?: (path: string) => string,
  ): IAxiosRequesterConfig {
    return {
      ...super.makeConfig(method, url, params, overrides, endpointBuilder),
      paramsSerializer: (p) => qs.stringify(p, this.baseURI.href() ? { arrayFormat: 'brackets', allowDots: true } : {}),
    };
  }
}
